/* eslint-disable */
// @ts-nocheck
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { get } from 'lodash-es';
import { applySearch, applySort, paginate } from '@/app/shared/demo-data';
import type {
  ItemsListQuery,
  ItemsListResponse,
  MissingItem,
  PaginationQuery,
  PaginatedResponse,
  SimpleItemsListResponse,
} from '@/app/shared/types';
import allocationGroups from '@/mock-data/allocations/groups.json';
import {
  getFlatInventoryOptions,
  getInsufficientOverviewFromFlat,
  getInsufficientSummaryFromFlat,
  getReplenishmentInventoryDetailsFromFlat,
  getReplenishmentInventoriesFromFlat,
  getReplenishmentLocationProductsFromFlat,
  getReplenishmentLocationsFromFlat,
  getReplenishmentOverviewFromFlat,
  getReplenishmentProductDetailsFromFlat,
  getReplenishmentProductLocationsFromFlat,
  getReplenishmentProductsFromFlat,
  getReplenishmentProductSkuDetailsFromFlat,
  getReplenishmentProductSkuDetailsInLocationFromFlat,
} from '@/app/shared/replenishment-flat';
import {
  getInventoryDetailsFromFlat,
  getInventoryInventoriesFromFlat,
  getInventoryLocationsFromFlat,
  getInventoryOptionsFromFlat,
  getInventoryProductLocationsFromFlat,
  getInventorySkusFromFlat,
} from '@/app/shared/inventory-flat';
import {
  getAllocationsAssortmentGroupLocationsFromFlat,
  getAllocationsAssortmentGroupsFromFlat,
  getAllocationsFilterOptionsFromFlat,
  getAllocationsInventoryLocationsFromFlat,
  getAllocationsComparableProductsCatalogFromFlat,
  getAllocationsComparableProductsFromFlat,
  getAllocationsProductKpiFromFlat,
  getAllocationsProductLocationDetailsFromFlat,
  getAllocationsProductLocationsFromFlat,
  getAllocationsProductLocationsListFromFlat,
  getAllocationsProductSummaryFromFlat,
  getAllocationsProductsFromFlat,
  getAllocationsProductsListFromFlat,
  getAllocationsProductsNavigationFromFlat,
  getAllocationsSimilarProductsFromFlat,
  applyAllocationsSizeOverrides,
  applyAllocationsSkuOverrides,
} from '@/app/shared/allocations-flat';
import {
  getSpecialEventsFromFlat,
  getSpecialEventDetailsFromFlat,
  getSpecialEventInventoriesFromFlat,
  getSpecialEventsAnalysisDashboardFromFlat,
  getSpecialEventsAnalysisFilterOptionsFromFlat,
} from '@/app/shared/special-events-flat';
type Method = 'get' | 'post' | 'put' | 'patch' | 'delete';
type ReplenishmentLocationType = string;
type ReplenishmentTimeFrame = string;
type ReplenishableType = string;
type AvoidReplenishmentState = Record<string, unknown>;
type InsufficientReplenishmentSummary = Record<string, unknown>;
type ReplenishmentInventory = Record<string, unknown>;
type ReplenishmentKpis = Record<string, unknown>;
type ReplenishmentLocation = Record<string, unknown>;
type ReplenishmentOverview = Record<string, unknown>;
type ReplenishmentProduct = Record<string, unknown>;
type ReplenishmentProductSkuDetails = Record<string, unknown>;
type ReplenishmentProductSkuDetailsInLocation = Record<string, unknown>;
type ReplenishmentProductWithDetails = Record<string, unknown>;
type InventoryReplenishmentScope = 'skus' | 'inventories' | 'locations';
type InventoryReplenishmentStatusUpdate = {
  id: string;
  created_at: string;
  updated_at: string;
  status: string;
  message: string;
  trigger: 'query' | 'upload';
  file_name: string | null;
  file_size: number | null;
  total_rows: number | null;
  failed_rows: number | null;
  error_file: string | null;
};
type InventoryDetails = Record<string, unknown>;
type InventoryTransactionsDashboardData = Record<string, unknown>;
type SupersetDashboardData = {
  uuid: string;
  module_tab: string;
  title: string;
};
type AllocationsGroup = Record<string, unknown>;
type AllocationsInfo = Record<string, unknown>;
type AllocationsProductSimilarities = Record<string, unknown>;
type AllocationsSettings = Record<string, unknown>;
let allocationsCalculationInProgress = false;
function buildResponse<T>(data: T, config?: AxiosRequestConfig): AxiosResponse<T> {
  return {
    data,
    status: 200,
    statusText: 'OK',
    headers: {},
    config: (config ?? {}) as any,
  };
}
function normalizePath(url: string) {
  const trimmed = url.trim();
  if (trimmed.startsWith('http')) {
    try {
      return new URL(trimmed).pathname;
    } catch (error) {
      return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    }
  }
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}
function parseQuery(url: string) {
  const queryIndex = url.indexOf('?');
  if (queryIndex === -1) {
    return {};
  }
  const params = new URLSearchParams(url.slice(queryIndex + 1));
  const result: Record<string, string> = {};
  params.forEach((value, key) => {
    result[key] = value;
  });
  return result;
}
function mergeParams(config?: AxiosRequestConfig, url?: string) {
  const urlParams = url ? parseQuery(url) : {};
  return {
    ...urlParams,
    ...(config?.params ?? {}),
  } as Record<string, any>;
}
function filterByLocationType<T>(items: T[], locationType?: ReplenishmentLocationType) {
  if (!locationType) {
    return items;
  }
  return items.filter((item) => {
    const value = (
      item as {
        location_type?: string;
      }
    ).location_type;
    return !value || value === locationType;
  });
}
function filterByTimeFrame<T>(items: T[], timeFrame?: ReplenishmentTimeFrame) {
  if (!timeFrame || timeFrame === 'all') {
    return items;
  }
  return items.filter((item) => {
    const replenishmentTime = (
      item as {
        replenishment_time?: number | number[];
      }
    ).replenishment_time;
    if (replenishmentTime == null) {
      return true;
    }
    const value = Array.isArray(replenishmentTime)
      ? Math.min(...replenishmentTime)
      : replenishmentTime;
    if (Number.isNaN(value)) {
      return true;
    }
    return timeFrame === 'today' ? value <= 1 : value > 1;
  });
}
function listResponse<T>(items: T[], params?: ItemsListQuery & PaginationQuery) {
  const filtered = applyFilterString(items, params?.filter);
  const searched = applySearch(
    filtered,
    params?.search,
    Object.keys(items[0] ?? {}) as (keyof T)[],
  );
  const sortOrder =
    params?.sortOrder === 'asc' || params?.sortOrder === 'desc' ? params.sortOrder : undefined;
  const sorted = applySort(searched, params?.sortBy as keyof T, sortOrder);
  return paginate(sorted, params?.limit ?? 25, params?.offset ?? 0) as ItemsListResponse<T>;
}
function paginatedResponse<T>(items: T[], params?: PaginationQuery) {
  const filtered = applyFilterString(
    items,
    (
      params as
        | {
            filter?: string;
          }
        | undefined
    )?.filter,
  );
  const source = filtered ?? items;
  const page = params?.page ?? 1;
  const perPage = params?.per_page ?? 25;
  const start = (page - 1) * perPage;
  const slice = source.slice(start, start + perPage);
  return {
    data: slice,
    meta: {
      page,
      per_page: perPage,
      total: source.length,
    },
  } satisfies PaginatedResponse<T>;
}
type FilterPredicate<T> = (item: T) => boolean;
const FILTER_FIELD_ALIASES: Record<string, string[]> = {
  department_id: ['department_name', 'department', 'departments', 'department_names'],
  category_id: ['category_name', 'category_names', 'categories'],
  optimal_quantity: ['total_single_optimal_quantity', 'total_single_optimal', 'optimal'],
  recommended_quantity: ['total_single_recommended_quantity', 'total_single_recommended', 'units'],
  similar_products_sale_rate: [
    'similar_products_sales_rate',
    'similar_product_sales_rate',
    'avg_sale_rate',
  ],
  source_location_id: ['source_location_id', 'source_location_external_id', 'source_location_name'],
  assortment_group: ['assortment_group_name', 'assortment_group', 'assortment_groups'],
  departments: ['department', 'departments', 'department_name', 'department_names'],
  categories: ['category', 'categories', 'category_name', 'category_names'],
  seasons: ['season', 'seasons'],
};
const CITY_OPTIONS = [
  'New York',
  'Los Angeles',
  'Chicago',
  'Houston',
  'Miami',
  'Seattle',
  'Denver',
  'Atlanta',
  'Boston',
  'San Francisco',
];
const REGION_OPTIONS = ['Northeast', 'West', 'Midwest', 'South', 'Southwest', 'Northwest'];
function hashString(value: string) {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) % 1000000;
  }
  return hash;
}
function getCityRegion(storeId: string) {
  const hash = hashString(storeId);
  return {
    city: CITY_OPTIONS[hash % CITY_OPTIONS.length],
    region: REGION_OPTIONS[hash % REGION_OPTIONS.length],
  };
}
function applyFilterString<T>(items: T[], filter?: string): T[] {
  if (!filter) {
    return items;
  }
  const expressions = filter
    .split(/\s+AND\s+/i)
    .map((expression) => expression.trim())
    .filter(Boolean);
  if (!expressions.length) {
    return items;
  }
  const predicates = expressions
    .map((expression) => parseFilterExpression<T>(expression))
    .filter((predicate): predicate is FilterPredicate<T> => Boolean(predicate));
  if (!predicates.length) {
    return items;
  }
  return items.filter((item) => predicates.every((predicate) => predicate(item)));
}
function parseFilterExpression<T>(expression: string): FilterPredicate<T> | null {
  let text = expression.trim();
  while (text.startsWith('(') && text.endsWith(')')) {
    text = text.slice(1, -1).trim();
  }
  text = text.replace(/^\(+/, '').replace(/\)+$/, '').trim();
  text = text.replace(/NOT_IN/g, 'NOT IN');
  const isNotNullMatch = text.match(/^(.+?)\s+IS\s+NOT\s+NULL$/i);
  if (isNotNullMatch) {
    const key = isNotNullMatch[1]!.trim();
    return (item) => resolveFieldValue(item, key) != null;
  }
  const isNullMatch = text.match(/^(.+?)\s+IS\s+NULL$/i);
  if (isNullMatch) {
    const key = isNullMatch[1]!.trim();
    return (item) => resolveFieldValue(item, key) == null;
  }
  const isEmptyMatch = text.match(/^(.+?)\s+IS\s+EMPTY$/i);
  if (isEmptyMatch) {
    const key = isEmptyMatch[1]!.trim();
    return (item) => {
      const value = resolveFieldValue(item, key);
      if (value == null) {
        return true;
      }
      if (Array.isArray(value)) {
        return value.length === 0;
      }
      return String(value).trim().length === 0;
    };
  }
  const isNotEmptyMatch = text.match(/^(.+?)\s+IS\s+NOT\s+EMPTY$/i);
  if (isNotEmptyMatch) {
    const key = isNotEmptyMatch[1]!.trim();
    return (item) => {
      const value = resolveFieldValue(item, key);
      if (value == null) {
        return false;
      }
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return String(value).trim().length > 0;
    };
  }
  const notInRange = parseNotInRange(text);
  if (notInRange) {
    const { key, from, to } = notInRange;
    return (item) => {
      const value = toNumber(resolveFieldValue(item, key));
      if (value == null) {
        return false;
      }
      return value < from || value > to;
    };
  }
  const inRange = parseInRange(text);
  if (inRange) {
    const { key, from, to } = inRange;
    return (item) => {
      const value = toNumber(resolveFieldValue(item, key));
      if (value == null) {
        return false;
      }
      return value >= from && value <= to;
    };
  }
  const notInMatch = text.match(/^(.+?)\s+NOT IN\s+(\[.+\])$/i);
  if (notInMatch) {
    const key = notInMatch[1]!.trim();
    const values = parseList(notInMatch[2] ?? '[]');
    return (item) => !matchesAny(resolveFieldValue(item, key), values);
  }
  const inMatch = text.match(/^(.+?)\s+IN\s+(\[.+\])$/i);
  if (inMatch) {
    const key = inMatch[1]!.trim();
    const values = parseList(inMatch[2] ?? '[]');
    return (item) => matchesAny(resolveFieldValue(item, key), values);
  }
  const eqMatch = text.match(/^(.+?)\s+=\s+(.+)$/);
  if (eqMatch) {
    const key = eqMatch[1]!.trim();
    const value = parseScalar(eqMatch[2]);
    return (item) => matchesScalar(resolveFieldValue(item, key), value);
  }
  const neqMatch = text.match(/^(.+?)\s+!=\s+(.+)$/);
  if (neqMatch) {
    const key = neqMatch[1]!.trim();
    const value = parseScalar(neqMatch[2]);
    return (item) => !matchesScalar(resolveFieldValue(item, key), value);
  }
  const containsMatch = text.match(/^(.+?)\s+CONTAINS\s+(.+)$/i);
  if (containsMatch) {
    const key = containsMatch[1]!.trim();
    const value = parseScalar(containsMatch[2]);
    return (item) => matchesContains(resolveFieldValue(item, key), value);
  }
  const notContainsMatch = text.match(/^(.+?)\s+NOT CONTAINS\s+(.+)$/i);
  if (notContainsMatch) {
    const key = notContainsMatch[1]!.trim();
    const value = parseScalar(notContainsMatch[2]);
    return (item) => !matchesContains(resolveFieldValue(item, key), value);
  }
  const startsMatch = text.match(/^(.+?)\s+STARTS WITH\s+(.+)$/i);
  if (startsMatch) {
    const key = startsMatch[1]!.trim();
    const value = parseScalar(startsMatch[2]);
    return (item) => matchesStartsWith(resolveFieldValue(item, key), value);
  }
  const notStartsMatch = text.match(/^(.+?)\s+NOT STARTS WITH\s+(.+)$/i);
  if (notStartsMatch) {
    const key = notStartsMatch[1]!.trim();
    const value = parseScalar(notStartsMatch[2]);
    return (item) => !matchesStartsWith(resolveFieldValue(item, key), value);
  }
  const lteMatch = text.match(/^(.+?)\s+<=\s+(.+)$/);
  if (lteMatch) {
    const key = lteMatch[1]!.trim();
    const value = Number(lteMatch[2]);
    return (item) => compareNumber(resolveFieldValue(item, key), value, (a, b) => a <= b);
  }
  const gteMatch = text.match(/^(.+?)\s+>=\s+(.+)$/);
  if (gteMatch) {
    const key = gteMatch[1]!.trim();
    const value = Number(gteMatch[2]);
    return (item) => compareNumber(resolveFieldValue(item, key), value, (a, b) => a >= b);
  }
  const ltMatch = text.match(/^(.+?)\s+<\s+(.+)$/);
  if (ltMatch) {
    const key = ltMatch[1]!.trim();
    const value = Number(ltMatch[2]);
    return (item) => compareNumber(resolveFieldValue(item, key), value, (a, b) => a < b);
  }
  const gtMatch = text.match(/^(.+?)\s+>\s+(.+)$/);
  if (gtMatch) {
    const key = gtMatch[1]!.trim();
    const value = Number(gtMatch[2]);
    return (item) => compareNumber(resolveFieldValue(item, key), value, (a, b) => a > b);
  }
  return null;
}
function parseInRange(text: string): {
  key: string;
  from: number;
  to: number;
} | null {
  const match = text.match(/\s+TO\s+/i);
  if (!match) {
    return null;
  }
  const parts = text.split(/\s+TO\s+/i);
  if (parts.length !== 2) {
    return null;
  }
  const left = parts[0]?.trim() ?? '';
  const right = parts[1]?.trim() ?? '';
  const tokens = left.split(/\s+/);
  if (tokens.length < 2) {
    return null;
  }
  const fromToken = tokens.pop() ?? '';
  const key = tokens.join(' ').trim();
  const from = Number(fromToken);
  const to = Number(right);
  if (!key || Number.isNaN(from) || Number.isNaN(to)) {
    return null;
  }
  return { key, from, to };
}
function parseNotInRange(text: string): {
  key: string;
  from: number;
  to: number;
} | null {
  const parts = text.split(/\s+OR\s+/i);
  if (parts.length !== 2) {
    return null;
  }
  const left = parts[0]?.trim() ?? '';
  const right = parts[1]?.trim() ?? '';
  const leftMatch = left.match(/^(.+?)\s+<\s+(.+)$/);
  const rightMatch = right.match(/^(.+?)\s+>\s+(.+)$/);
  if (!leftMatch || !rightMatch) {
    return null;
  }
  const keyLeft = leftMatch[1]!.trim();
  const keyRight = rightMatch[1]!.trim();
  if (keyLeft !== keyRight) {
    return null;
  }
  const from = Number(leftMatch[2]);
  const to = Number(rightMatch[2]);
  if (Number.isNaN(from) || Number.isNaN(to)) {
    return null;
  }
  return { key: keyLeft, from, to };
}
function extractAllocationGroupId(filter?: string): string | null {
  if (!filter) {
    return null;
  }
  const nullMatch = filter.match(/allocation_group_id\s+IS\s+NULL/i);
  if (nullMatch) {
    return null;
  }
  const match = filter.match(/allocation_group_id\s*=\s*"?([^"\s)]+)"?/i);
  return match ? (match[1] ?? null) : null;
}
function resolveAllocationGroupId(params: Record<string, any>): string | null {
  const fromParam = params.allocationsGroupId ?? params.allocation_group_id ?? null;
  if (typeof fromParam === 'string' && fromParam.length > 0) {
    return fromParam;
  }
  return extractAllocationGroupId(params.filter as string | undefined);
}
function toNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const parsed = Number(
    String(value ?? '')
      .replace(/[$,%]/g, '')
      .replace(/,/g, '')
      .trim(),
  );
  return Number.isFinite(parsed) ? parsed : null;
}
function parseScalar(value: string): string {
  const trimmed = value.trim();
  if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return trimmed.slice(1, -1);
  }
  return trimmed;
}
function parseList(value: string): string[] {
  const trimmed = value.trim();
  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item));
      }
    } catch {}
  }
  return trimmed
    .replace(/^\[/, '')
    .replace(/\]$/, '')
    .split(',')
    .map((item) => item.trim().replace(/^"|"$/g, ''))
    .filter(Boolean);
}
function resolveFieldValue<T>(item: T, key: string): unknown {
  if (key === 'allocation_date_timestamp') {
    const direct = get(item, key);
    if (direct != null) {
      return direct;
    }
    const allocationDate = get(item, 'allocation_date');
    if (allocationDate) {
      const parsed = Date.parse(String(allocationDate));
      return Number.isNaN(parsed) ? null : Math.floor(parsed / 1000);
    }
    return null;
  }
  if (key === 'constraints') {
    const value = get(item, key);
    if (!Array.isArray(value)) {
      return value;
    }
    if (value.length === 0) {
      return [];
    }
    const names = value
      .map((entry) => {
        if (typeof entry === 'string') {
          return entry;
        }
        if (entry && typeof entry === 'object' && 'name' in entry) {
          return String(
            (
              entry as {
                name?: string;
              }
            ).name ?? '',
          );
        }
        return '';
      })
      .filter(Boolean);
    return names;
  }
  if (key === 'region' || key === 'city') {
    const direct = get(item, key);
    if (direct != null && String(direct).trim() !== '') {
      return direct;
    }
    const id = get(item, 'id') ?? get(item, 'location_id') ?? '';
    if (typeof id === 'string' && id) {
      const { city, region } = getCityRegion(id);
      return key === 'city' ? city : region;
    }
  }
  const direct = get(item, key);
  if (direct != null) {
    return direct;
  }
  const aliases = FILTER_FIELD_ALIASES[key] ?? [];
  for (const alias of aliases) {
    const value = get(item, alias);
    if (value != null) {
      return value;
    }
  }
  return null;
}
function normalize(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}
function matchesAny(value: unknown, values: string[]): boolean {
  if (value == null) {
    return false;
  }
  const normalizedValues = values.map((item) => item.toLowerCase());
  if (Array.isArray(value)) {
    return value.some((item) => normalizedValues.includes(normalize(item)));
  }
  return normalizedValues.includes(normalize(value));
}
function matchesScalar(value: unknown, expected: string): boolean {
  if (value == null) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.some((item) => normalize(item) === normalize(expected));
  }
  return normalize(value) === normalize(expected);
}
function matchesContains(value: unknown, expected: string): boolean {
  if (value == null) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.some((item) => normalize(item).includes(normalize(expected)));
  }
  return normalize(value).includes(normalize(expected));
}
function matchesStartsWith(value: unknown, expected: string): boolean {
  if (value == null) {
    return false;
  }
  if (Array.isArray(value)) {
    return value.some((item) => normalize(item).startsWith(normalize(expected)));
  }
  return normalize(value).startsWith(normalize(expected));
}
function compareNumber(
  value: unknown,
  expected: number,
  comparator: (left: number, right: number) => boolean,
): boolean {
  const actual = toNumber(value);
  if (actual == null || Number.isNaN(expected)) {
    return false;
  }
  return comparator(actual, expected);
}
function ensureExternalId<
  T extends {
    id: string;
    external_id?: string;
  },
>(items: T[]): T[] {
  return items.map((item) => ({
    ...item,
    external_id: item.external_id ?? item.id,
  }));
}
function recordByExternalId(items: ReplenishmentProductSkuDetails[]) {
  return items.reduce<Record<string, ReplenishmentProductSkuDetails>>((acc, item) => {
    acc[item.external_id] = item;
    return acc;
  }, {});
}
const MS_IN_DAY = 24 * 60 * 60 * 1000;
const inventoryAvoidReplenishmentState: Record<
  InventoryReplenishmentScope,
  Map<string, boolean>
> = {
  locations: new Map(),
  skus: new Map(),
  inventories: new Map(),
};
const inventoryAvoidReplenishmentUpdates = new Map<
  InventoryReplenishmentScope,
  InventoryReplenishmentStatusUpdate
>();
const inventoryAvoidReplenishmentUpdateTimers = new Map<
  InventoryReplenishmentScope,
  ReturnType<typeof setTimeout>
>();
function extractIdsFromQuery(query?: string): string[] {
  if (!query) {
    return [];
  }
  const match = query.match(/id\s+IN\s*\[([^\]]+)\]/i);
  if (!match) {
    return [];
  }
  return match[1]
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
}
function buildInventoryTransactionsDashboard(
  params: Record<string, any>,
): InventoryTransactionsDashboardData {
  const parseDate = (value: string | undefined) => {
    if (!value) {
      return null;
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };
  const endDate = parseDate(params.endDate) ?? new Date();
  const startDate = parseDate(params.startDate) ?? new Date(endDate.getTime() - 59 * MS_IN_DAY);
  const safeStart = startDate.getTime() <= endDate.getTime() ? startDate : endDate;
  const safeEnd = startDate.getTime() <= endDate.getTime() ? endDate : startDate;
  const totalDays = Math.max(
    1,
    Math.floor((safeEnd.getTime() - safeStart.getTime()) / MS_IN_DAY) + 1,
  );
  const dailySums = Array.from({ length: totalDays }, (_, index) => {
    const date = new Date(safeStart.getTime() + index * MS_IN_DAY);
    const baseUnits = 20 + (index % 7) * 3;
    const salesUnits = 28 + (index % 5) * 4;
    const returnsUnits = index % 4;
    const inUnits = baseUnits + 6;
    const outUnits = salesUnits + 2;
    const sumSalesValue = salesUnits * 38;
    const sumReturnsValue = returnsUnits * 12;
    const sumNetSalesValue = sumSalesValue - sumReturnsValue;
    const sumInValue = inUnits * 20;
    const sumOutValue = outUnits * 24;
    return {
      date: date.toISOString().slice(0, 10),
      sumInUnitsBrackets: inUnits,
      sumInValue,
      sumNetSalesValue,
      sumOutUnitsBrackets: outUnits,
      sumOutValue,
      sumReturnsValue,
      sumSalesUnitsBrackets: salesUnits,
      sumSalesValue,
      throughputSum: sumSalesValue + sumInValue,
    };
  });
  const totalSales = dailySums.reduce((sum, item) => sum + item.sumSalesValue, 0);
  const averageDailySales = totalSales / dailySums.length;
  const lastDay = dailySums[dailySums.length - 1];
  const weekBuckets = Array.from({ length: 7 }, () => ({ sum: 0, count: 0 }));
  const monthBuckets = Array.from({ length: 12 }, () => ({ sum: 0, count: 0 }));
  dailySums.forEach((item) => {
    const date = new Date(item.date);
    const weekday = date.getUTCDay();
    const month = date.getUTCMonth();
    weekBuckets[weekday].sum += item.sumSalesValue;
    weekBuckets[weekday].count += 1;
    monthBuckets[month].sum += item.sumSalesValue;
    monthBuckets[month].count += 1;
  });
  const weekDistribution = weekBuckets.map((bucket, index) => ({
    weekday: index as InventoryTransactionsDashboardData['weekDistribution'][number]['weekday'],
    value: bucket.count ? bucket.sum / bucket.count : 0,
  }));
  const monthDistribution = monthBuckets.map((bucket) =>
    bucket.count ? bucket.sum / bucket.count : 0,
  );
  const orderedSalesDates = dailySums
    .map((item) => {
      const date = new Date(item.date);
      const month = date.getUTCMonth();
      return {
        date: item.date,
        weekday:
          date.getUTCDay() as InventoryTransactionsDashboardData['orderedSalesDates'][number]['weekday'],
        quarter: Math.floor(month / 3) + 1,
        sumSalesValue: item.sumSalesValue,
        sumSalesUnits: item.sumSalesUnitsBrackets,
        sumReturnsValue: item.sumReturnsValue,
        sumReturnsUnits: item.sumReturnsValue ? Math.round(item.sumReturnsValue / 12) : 0,
        sumNetSales: item.sumNetSalesValue,
      };
    })
    .sort((a, b) => b.sumSalesValue - a.sumSalesValue);
  return {
    averageDailySales,
    lastDaySales: lastDay?.sumSalesValue ?? 0,
    lastDayNetSales: lastDay?.sumNetSalesValue ?? 0,
    dailySums,
    weekDistribution,
    monthDistribution,
    orderedSalesDates,
  };
}
async function handleReplenishment(
  method: Method,
  path: string,
  config?: AxiosRequestConfig,
  data?: any,
) {
  const params = mergeParams(config, path);
  const cleanPath = normalizePath(path).split('?')[0];
  if (method === 'get' && cleanPath === '/v1/products/sku_details') {
    const productId = params.id as string;
    const details = await getReplenishmentProductSkuDetailsFromFlat();
    return buildResponse(recordByExternalId(details[productId] ?? []), config);
  }
  if (method === 'get' && cleanPath === '/v1/products/inventories_list_by_location') {
    const productId = params.id as string;
    const locations = await getReplenishmentProductLocationsFromFlat();
    const locationsForProduct = locations[productId] ?? [];
    const result = locationsForProduct.reduce<Record<string, string[]>>((acc, item) => {
      acc[item.id] = item.inventory_ids;
      return acc;
    }, {});
    return buildResponse(result, config);
  }
  if (method === 'get' && cleanPath === '/v1/products') {
    const products = await getReplenishmentProductsFromFlat();
    const filtered = filterByTimeFrame(products, params.time_frame as ReplenishmentTimeFrame);
    return buildResponse(paginatedResponse(filtered, params), config);
  }
  if (method === 'post' && cleanPath === '/v1/products/by_ids') {
    const products = await getReplenishmentProductsFromFlat();
    const ids = (data?.ids ?? []) as string[];
    const response = ids.map(
      (id) =>
        products.find((item) => item.id === id) ?? ({ id, missing: true } satisfies MissingItem),
    );
    return buildResponse(
      { data: response } as {
        data: (ReplenishmentProduct | MissingItem)[];
      },
      config,
    );
  }
  if (method === 'get' && cleanPath.startsWith('/v1/products/sku_details_by_store/')) {
    const [, , , , rawProductId, rawLocationId] = cleanPath.split('/');
    const productId = rawProductId ? decodeURIComponent(rawProductId) : '';
    const locationId = rawLocationId ? decodeURIComponent(rawLocationId) : '';
    const details = await getReplenishmentProductSkuDetailsInLocationFromFlat();
    const key = `${productId}:${locationId}`;
    const keys = Object.keys(details);
    const countByStore = keys.filter((item) => item.endsWith(`:${locationId}`)).length;
    const countByProduct = keys.filter((item) => item.startsWith(`${productId}:`)).length;
    console.info('[demo-proxy] sku_details_by_store request', {
      productId,
      locationId,
      key,
      countByStore,
      countByProduct,
    });
    if (details[key]?.length) {
      return buildResponse(
        { data: details[key] } as {
          data: ReplenishmentProductSkuDetailsInLocation[];
        },
        config,
      );
    }
    const inventories = await getReplenishmentInventoriesFromFlat();
    const normalize = (value?: string | null) =>
      String(value ?? '')
        .trim()
        .toLowerCase();
    const normalizeLoose = (value?: string | null) => normalize(value).replace(/[^a-z0-9]/g, '');
    const targetProduct = normalize(productId);
    const targetLocation = normalize(locationId);
    const targetProductLoose = normalizeLoose(productId);
    const targetLocationLoose = normalizeLoose(locationId);
    const fallback = inventories
      .filter((item) => {
        const productMatch =
          normalize(item.product_id) === targetProduct ||
          normalize(item.product_external_id) === targetProduct ||
          normalize(item.product_name) === targetProduct ||
          normalizeLoose(item.product_id) === targetProductLoose ||
          normalizeLoose(item.product_external_id) === targetProductLoose ||
          normalizeLoose(item.product_name) === targetProductLoose;
        const locationMatch =
          normalize(item.location_id) === targetLocation ||
          normalize(item.location_external_id) === targetLocation ||
          normalize(item.location_name) === targetLocation ||
          normalizeLoose(item.location_id) === targetLocationLoose ||
          normalizeLoose(item.location_external_id) === targetLocationLoose ||
          normalizeLoose(item.location_name) === targetLocationLoose;
        return productMatch && locationMatch;
      })
      .map((item) => ({
        id: item.id,
        sku_id: item.sku_id,
        sku_external_id: item.sku_external_id,
        sku_name: item.sku_name ?? item.sku_external_id,
        size: item.size_name ?? '',
        replenishment: item.replenishment,
        sale_rate: item.sale_rate ?? 0,
        site_qty: item.site_qty ?? 0,
        transit_qty: item.transit_qty ?? 0,
        optimal_stock: item.optimal_stock ?? 0,
        sold: item.sold ?? 0,
        wh_names: [],
        next_replenishment: '',
        wh_qty: item.wh_inventory ?? 0,
        coverage: item.coverage ?? 0,
        expected_coverage: item.coverage ?? 0,
        constraints: item.constraints,
        last_in_on: null,
        master_sku_external_id: item.master_sku_external_id,
        master_sku_name: item.master_sku_name,
      }));
    console.info('[demo-proxy] sku_details_by_store fallback', {
      productId,
      locationId,
      key,
      fallbackCount: fallback.length,
      totalInventories: inventories.length,
    });
    return buildResponse(
      { data: fallback } as {
        data: ReplenishmentProductSkuDetailsInLocation[];
      },
      config,
    );
  }
  if (method === 'get' && cleanPath.startsWith('/v1/products/')) {
    const parts = cleanPath.split('/');
    if (parts.length === 4) {
      const id = parts[3] ?? '';
      const details = await getReplenishmentProductDetailsFromFlat();
      const product = details.find((item) => item.id === id) ?? null;
      return buildResponse(
        { data: product } as {
          data: ReplenishmentProductWithDetails | null;
        },
        config,
      );
    }
  }
  if (method === 'get' && cleanPath === '/v1/replenishments/locations') {
    const locations = await getReplenishmentLocationsFromFlat();
    const filtered = filterByTimeFrame(
      filterByLocationType(locations, params.location_type as ReplenishmentLocationType),
      params.time_frame as ReplenishmentTimeFrame,
    );
    return buildResponse(paginatedResponse(filtered, params), config);
  }
  if (method === 'post' && cleanPath === '/v1/replenishments/locations/by_ids') {
    const locations = await getReplenishmentLocationsFromFlat();
    const ids = (data?.ids ?? []) as string[];
    const filtered = filterByLocationType(
      locations.filter((item) => ids.includes(item.id)),
      data?.location_type as ReplenishmentLocationType,
    );
    return buildResponse(
      { data: filtered } as {
        data: ReplenishmentLocation[];
      },
      config,
    );
  }
  if (method === 'get' && cleanPath === '/v1/replenishments/inventories') {
    const inventories = await getReplenishmentInventoriesFromFlat();
    const filtered = filterByTimeFrame(
      filterByLocationType(inventories, params.location_type as ReplenishmentLocationType),
      params.time_frame as ReplenishmentTimeFrame,
    );
    return buildResponse(paginatedResponse(filtered, params), config);
  }
  if (method === 'post' && cleanPath === '/v1/replenishments/inventories/by_ids') {
    const inventories = await getReplenishmentInventoriesFromFlat();
    const ids = (data?.ids ?? []) as string[];
    const filtered = filterByTimeFrame(
      filterByLocationType(inventories, data?.location_type as ReplenishmentLocationType),
      data?.time_frame as ReplenishmentTimeFrame,
    ).filter((item) => ids.includes(item.id));
    return buildResponse(
      { data: filtered } as {
        data: ReplenishmentInventory[];
      },
      config,
    );
  }
  if (method === 'get' && cleanPath.startsWith('/v1/replenishments/inventories/')) {
    const id = cleanPath.split('/').pop() ?? '';
    const details = await getReplenishmentInventoryDetailsFromFlat(id);
    return buildResponse(details, config);
  }
  if (method === 'get' && cleanPath === '/v1/replenishments/kpis') {
    const locations = await getReplenishmentLocationsFromFlat();
    const filtered = filterByLocationType(
      locations,
      params.location_type as ReplenishmentLocationType,
    );
    const totals = filtered.reduce(
      (acc, item) => {
        acc.total_replenishment += item.replenishment;
        acc.total_products += item.total_products;
        acc.total_skus += item.total_skus;
        if (item.location_type === 'store') acc.total_stores += 1;
        if (item.location_type === 'warehouse') acc.total_wh += 1;
        return acc;
      },
      {
        total_products: 0,
        total_replenishment: 0,
        total_skus: 0,
        total_stores: 0,
        total_wh: 0,
      },
    );
    return buildResponse(totals as ReplenishmentKpis, config);
  }
  if (method === 'get' && cleanPath === '/v1/replenishments/overview') {
    const overview = await getReplenishmentOverviewFromFlat();
    const locationType = params.location_type as ReplenishmentLocationType | undefined;
    if (!locationType) {
      return buildResponse(overview, config);
    }
    const allowed = new Set(
      (await getReplenishmentLocationsFromFlat())
        .filter((item) => item.location_type === locationType)
        .map((item) => item.id),
    );
    const filtered: ReplenishmentOverview = {
      replenishment: overview.replenishment.filter((item) => allowed.has(item.location_id)),
      sold: overview.sold.filter((item) => allowed.has(item.location_id)),
      sold_depleted: overview.sold_depleted.filter((item) => allowed.has(item.location_id)),
    };
    return buildResponse(filtered, config);
  }
  if (method === 'get' && cleanPath === '/v1/replenishments/picking_list.csv') {
    const inventories = await getReplenishmentInventoriesFromFlat();
    const headers = ['location', 'sku', 'replenishment'];
    const rows = inventories
      .slice(0, 50)
      .map((item) =>
        [item.location_external_id, item.sku_external_id, item.replenishment].join(','),
      );
    const csv = [headers.join(','), ...rows].join('\n');
    return buildResponse(new Blob([csv], { type: 'text/csv' }), config);
  }
  if (method === 'get' && cleanPath === '/v1/replenishments/picking_list.json') {
    return buildResponse({ data: { generated_at: new Date().toISOString() } }, config);
  }
  if (
    method === 'get' &&
    cleanPath.startsWith('/v1/replenishments/insufficient_replenishment/card/')
  ) {
    const inventoryId = cleanPath.split('/').pop() ?? '';
    const summaryMap = await getInsufficientSummaryFromFlat();
    return buildResponse(
      { data: summaryMap[inventoryId] } as {
        data: InsufficientReplenishmentSummary;
      },
      config,
    );
  }
  if (
    method === 'get' &&
    cleanPath.startsWith('/v1/replenishments/insufficient_replenishment/overview/')
  ) {
    const inventoryId = cleanPath.split('/').pop() ?? '';
    const overviewMap = await getInsufficientOverviewFromFlat();
    return buildResponse(overviewMap[inventoryId] ?? [], config);
  }
  if (
    method === 'put' &&
    cleanPath.startsWith('/v1/') &&
    cleanPath.endsWith('/avoid_replenishment')
  ) {
    const type = cleanPath.split('/')[2] as ReplenishableType;
    const ids = Array.isArray(data?.ids) ? data.ids : JSON.parse(data?.ids ?? '[]');
    const response = (ids as string[]).map((id) => ({
      id,
      avoid_replenishment: true,
    })) as AvoidReplenishmentState[];
    return buildResponse({ data: response, type }, config);
  }
  if (
    method === 'get' &&
    cleanPath.startsWith('/v1/replenishments/products/') &&
    cleanPath.endsWith('/locations')
  ) {
    const productId = cleanPath.split('/')[4];
    const locationsMap = await getReplenishmentProductLocationsFromFlat();
    const items = locationsMap[productId] ?? [];
    return buildResponse(paginatedResponse(items, { page: 1, per_page: items.length }), config);
  }
  if (method === 'patch' && cleanPath === '/v1/replenishments/bulk_update') {
    return buildResponse({ success: true }, config);
  }
  if (method === 'patch' && cleanPath === '/v1/replenishments/bulk_reset') {
    return buildResponse({ success: true }, config);
  }
  if (method === 'get' && cleanPath === '/replenishment/inventories') {
    const inventories = await getReplenishmentInventoriesFromFlat();
    const filtered = filterByTimeFrame(
      filterByLocationType(inventories, params.location_type as ReplenishmentLocationType),
      params.time_frame as ReplenishmentTimeFrame,
    );
    return buildResponse(listResponse(filtered, params), config);
  }
  if (method === 'get' && cleanPath === '/replenishment/locations') {
    const view = params.view as string | undefined;
    if (view === 'minimal') {
      const { locations } = await getFlatInventoryOptions();
      return buildResponse(listResponse(locations, params), config);
    }
    const locations = await getReplenishmentLocationsFromFlat();
    const filtered = filterByTimeFrame(
      filterByLocationType(locations, params.location_type as ReplenishmentLocationType),
      params.time_frame as ReplenishmentTimeFrame,
    );
    return buildResponse(listResponse(filtered, params), config);
  }
  if (method === 'post' && cleanPath === '/replenishment/locations/by-ids') {
    const ids = (data?.ids ?? []) as string[];
    const locations = await getReplenishmentLocationsFromFlat();
    const filtered = locations.filter((item) => ids.includes(item.id));
    return buildResponse(filtered, config);
  }
  if (method === 'get' && cleanPath === '/replenishment/products') {
    const view = params.view as string | undefined;
    if (view === 'minimal') {
      const { products } = await getFlatInventoryOptions();
      return buildResponse(listResponse(products, params), config);
    }
    const products = await getReplenishmentProductsFromFlat();
    const filtered = filterByTimeFrame(products, params.time_frame as ReplenishmentTimeFrame);
    return buildResponse(listResponse(filtered, params), config);
  }
  if (method === 'post' && cleanPath === '/replenishment/products/by-ids') {
    const ids = (data?.ids ?? []) as string[];
    const { products } = await getFlatInventoryOptions();
    return buildResponse(
      products.filter((item) => ids.includes(item.id)),
      config,
    );
  }
  if (method === 'get' && cleanPath === '/replenishment/skus') {
    const { skus } = await getFlatInventoryOptions();
    return buildResponse(listResponse(skus, params), config);
  }
  if (method === 'post' && cleanPath === '/replenishment/skus/by-ids') {
    const ids = (data?.ids ?? []) as string[];
    const { skus } = await getFlatInventoryOptions();
    return buildResponse(
      skus.filter((item) => ids.includes(item.id)),
      config,
    );
  }
  if (method === 'get' && cleanPath === '/replenishment/brands') {
    const { brands } = await getFlatInventoryOptions();
    return buildResponse(listResponse(brands, params), config);
  }
  if (method === 'get' && cleanPath === '/replenishment/seasons') {
    const { seasons } = await getFlatInventoryOptions();
    return buildResponse(listResponse(seasons, params), config);
  }
  if (method === 'get' && cleanPath === '/replenishment/styles') {
    const { styles } = await getFlatInventoryOptions();
    return buildResponse(listResponse(styles, params), config);
  }
  if (method === 'get' && cleanPath === '/replenishment/departments') {
    const { departments } = await getFlatInventoryOptions();
    return buildResponse(listResponse(departments, params), config);
  }
  if (method === 'post' && cleanPath === '/replenishment/departments/by-ids') {
    const ids = (data?.ids ?? []) as string[];
    const { departments } = await getFlatInventoryOptions();
    return buildResponse(
      departments.filter((item) => ids.includes(item.id)),
      config,
    );
  }
  if (method === 'get' && cleanPath === '/replenishment/categories') {
    const { categories } = await getFlatInventoryOptions();
    return buildResponse(listResponse(categories, params), config);
  }
  if (method === 'get' && cleanPath === '/replenishment/regions') {
    const { regions } = await getFlatInventoryOptions();
    return buildResponse(listResponse(regions, params), config);
  }
  if (method === 'get' && cleanPath === '/replenishment/cities') {
    const { cities } = await getFlatInventoryOptions();
    return buildResponse(listResponse(cities, params), config);
  }
  if (
    method === 'get' &&
    cleanPath.startsWith('/replenishment/locations/') &&
    cleanPath.endsWith('/products')
  ) {
    const locationId = cleanPath.split('/')[3];
    const locationProducts = await getReplenishmentLocationProductsFromFlat();
    const items = locationProducts[locationId] ?? [];
    return buildResponse(listResponse(items, params), config);
  }
  return null;
}
async function handleInventory(
  method: Method,
  path: string,
  config?: AxiosRequestConfig,
  data?: any,
) {
  const params = mergeParams(config, path);
  const cleanPath = normalizePath(path).split('?')[0];
  if (method === 'get' && cleanPath === '/v1/supersets/dashboards') {
    const dashboards: SupersetDashboardData[] = [
      {
        default_dashboard: true,
        description: 'Inventory locations dashboard',
        locale: null,
        module_tab: 'inventory_location',
        name: 'Inventory Locations',
        uuid: 'demo-inventory-location',
      },
      {
        default_dashboard: true,
        description: 'Inventory SKUs dashboard',
        locale: null,
        module_tab: 'inventory_sku',
        name: 'Inventory SKUs',
        uuid: 'demo-inventory-sku',
      },
      {
        default_dashboard: true,
        description: 'Inventory inventories dashboard',
        locale: null,
        module_tab: 'inventory_inventories',
        name: 'Inventory Inventories',
        uuid: 'demo-inventory-inventories',
      },
    ];
    return buildResponse({ data: dashboards }, config);
  }
  if (method === 'post' && cleanPath.startsWith('/v1/supersets/guest_token/')) {
    return buildResponse({ token: 'demo-superset-token' }, config);
  }
  if (method === 'get' && cleanPath === '/analytics/dashboards/inventory-transactions') {
    return buildResponse(buildInventoryTransactionsDashboard(params), config);
  }
  if (method === 'get' && cleanPath.startsWith('/v1/inventories/')) {
    const id = cleanPath.split('/').pop() ?? '';
    const inventory = await getInventoryDetailsFromFlat(id);
    return buildResponse(inventory as InventoryDetails, config);
  }
  if (method === 'get' && cleanPath === '/v1/inventories') {
    const inventories = await getInventoryInventoriesFromFlat();
    const inventoryOverrides = inventoryAvoidReplenishmentState.inventories;
    const locationOverrides = inventoryAvoidReplenishmentState.locations;
    const skuOverrides = inventoryAvoidReplenishmentState.skus;
    const decorated = inventories.map((item) => ({
      ...item,
      avoid_replenishment: inventoryOverrides.get(item.id) ?? item.avoid_replenishment,
      location_avoid_replenishment:
        locationOverrides.get(item.location_id) ?? item.location_avoid_replenishment,
      sku_avoid_replenishment: skuOverrides.get(item.sku_id) ?? item.sku_avoid_replenishment,
    }));
    return buildResponse(paginatedResponse(decorated, params), config);
  }
  if (method === 'post' && cleanPath === '/v1/inventories/by_ids') {
    const inventories = await getInventoryInventoriesFromFlat();
    const ids = (data?.ids ?? []) as string[];
    const inventoryOverrides = inventoryAvoidReplenishmentState.inventories;
    const locationOverrides = inventoryAvoidReplenishmentState.locations;
    const skuOverrides = inventoryAvoidReplenishmentState.skus;
    const filtered = inventories
      .filter((item) => ids.includes(item.id))
      .map((item) => ({
        ...item,
        avoid_replenishment: inventoryOverrides.get(item.id) ?? item.avoid_replenishment,
        location_avoid_replenishment:
          locationOverrides.get(item.location_id) ?? item.location_avoid_replenishment,
        sku_avoid_replenishment: skuOverrides.get(item.sku_id) ?? item.sku_avoid_replenishment,
      }));
    return buildResponse({ data: filtered }, config);
  }
  if (method === 'get' && cleanPath === '/v1/locations') {
    const locations = await getInventoryLocationsFromFlat();
    const overrides = inventoryAvoidReplenishmentState.locations;
    const decorated = locations.map((item) => ({
      ...item,
      avoid_replenishment: overrides.get(item.id) ?? item.avoid_replenishment,
    }));
    return buildResponse(paginatedResponse(decorated, params), config);
  }
  if (method === 'post' && cleanPath === '/v1/locations/by_ids') {
    const locations = await getInventoryLocationsFromFlat();
    const ids = (data?.ids ?? []) as string[];
    const overrides = inventoryAvoidReplenishmentState.locations;
    const filtered = locations
      .filter((item) => ids.includes(item.id))
      .map((item) => ({
        ...item,
        avoid_replenishment: overrides.get(item.id) ?? item.avoid_replenishment,
      }));
    return buildResponse({ data: filtered }, config);
  }
  if (method === 'get' && cleanPath === '/v1/skus') {
    const skus = await getInventorySkusFromFlat();
    const overrides = inventoryAvoidReplenishmentState.skus;
    const decorated = skus.map((item) => ({
      ...item,
      avoid_replenishment: overrides.get(item.id) ?? item.avoid_replenishment,
    }));
    return buildResponse(paginatedResponse(decorated, params), config);
  }
  if (method === 'post' && cleanPath === '/v1/skus/by_ids') {
    const skus = await getInventorySkusFromFlat();
    const ids = (data?.ids ?? []) as string[];
    const overrides = inventoryAvoidReplenishmentState.skus;
    const filtered = skus
      .filter((item) => ids.includes(item.id))
      .map((item) => ({
        ...item,
        avoid_replenishment: overrides.get(item.id) ?? item.avoid_replenishment,
      }));
    return buildResponse({ data: filtered }, config);
  }
  if (method === 'get' && cleanPath === '/v1/inventories/kpis') {
    const inventories = await getInventoryInventoriesFromFlat();
    const options = await getInventoryOptionsFromFlat();
    return buildResponse(
      {
        locations: { store: options.locations.length },
        total_skus: options.skus.length,
        total_products: options.products.length,
        total_categories: options.categories.length,
        inventory: {
          sum_site_qty: inventories.reduce((sum, item) => sum + (item.site_qty ?? 0), 0),
          sum_transit_qty: inventories.reduce((sum, item) => sum + (item.transit_qty ?? 0), 0),
          sum_reserved_qty: inventories.reduce((sum, item) => sum + (item.reserved_qty ?? 0), 0),
        },
      },
      config,
    );
  }
  if (method === 'get' && cleanPath === '/v1/locations/kpis') {
    const inventories = await getInventoryInventoriesFromFlat();
    const locations = await getInventoryLocationsFromFlat();
    return buildResponse(
      {
        cost_in_transit: 0,
        cost_at_site: 0,
        cost_reserved: 0,
        inventory_cost: 0,
        total_inventory: inventories.reduce((sum, item) => sum + (item.site_qty ?? 0), 0),
        account_inventory: {
          sum_site_qty: inventories.reduce((sum, item) => sum + (item.site_qty ?? 0), 0),
          sum_transit_qty: inventories.reduce((sum, item) => sum + (item.transit_qty ?? 0), 0),
          sum_reserved_qty: inventories.reduce((sum, item) => sum + (item.reserved_qty ?? 0), 0),
        },
        locations: { store: locations.length },
        total_locations: locations.length,
      },
      config,
    );
  }
  if (method === 'get' && cleanPath === '/v1/skus/kpis') {
    const inventories = await getInventoryInventoriesFromFlat();
    const skus = await getInventorySkusFromFlat();
    return buildResponse(
      {
        total_skus: skus.length,
        total_products: new Set(skus.map((item) => item.product_id)).size,
        total_categories: new Set(skus.flatMap((item) => item.categories ?? [])).size,
        inventory_status: {
          stores: {
            sum_site_qty: inventories.reduce((sum, item) => sum + (item.site_qty ?? 0), 0),
            sum_transit_qty: inventories.reduce((sum, item) => sum + (item.transit_qty ?? 0), 0),
            sum_reserved_qty: inventories.reduce((sum, item) => sum + (item.reserved_qty ?? 0), 0),
          },
          warehouses: {
            sum_site_qty: 0,
            sum_transit_qty: 0,
            sum_reserved_qty: 0,
          },
        },
        daily_sales: 0,
      },
      config,
    );
  }
  if (method === 'get' && cleanPath.startsWith('/v1/locations/')) {
    const id = cleanPath.split('/').pop() ?? '';
    const locations = await getInventoryLocationsFromFlat();
    const location = locations.find((item) => item.id === id) ?? null;
    return buildResponse(location, config);
  }
  if (method === 'get' && cleanPath.startsWith('/v1/skus/')) {
    const id = cleanPath.split('/').pop() ?? '';
    const skus = await getInventorySkusFromFlat();
    const sku = skus.find((item) => item.id === id) ?? null;
    return buildResponse(sku, config);
  }
  if (method === 'patch' && cleanPath === '/v1/product_locations') {
    return buildResponse({ task_id: 'demo-product-locations-task' }, config);
  }
  if (method === 'patch' && cleanPath === '/v1/product_locations/by_query') {
    return buildResponse({ task_id: 'demo-product-locations-task' }, config);
  }
  if (method === 'get' && cleanPath === '/v1/product_locations/indexing_status') {
    return buildResponse({ in_progress: false }, config);
  }
  if (method === 'get' && cleanPath.startsWith('/v1/tasks/')) {
    return buildResponse({ state: 'completed' }, config);
  }
  if (method === 'post' && cleanPath === '/v1/settings/avoid_replenishments/query') {
    const scope = (data?.scope as InventoryReplenishmentScope) ?? 'inventories';
    const active = Boolean(data?.active);
    const query = typeof data?.query === 'string' ? data.query : '';
    const idsFromQuery = extractIdsFromQuery(query);
    const ids =
      idsFromQuery.length > 0
        ? idsFromQuery
        : scope === 'locations'
          ? (await getInventoryLocationsFromFlat()).map((item) => item.id)
          : scope === 'skus'
            ? (await getInventorySkusFromFlat()).map((item) => item.id)
            : (await getInventoryInventoriesFromFlat()).map((item) => item.id);
    const overrides = inventoryAvoidReplenishmentState[scope];
    const avoidReplenishment = !active;
    ids.forEach((id) => {
      overrides.set(id, avoidReplenishment);
    });
    const existingTimer = inventoryAvoidReplenishmentUpdateTimers.get(scope);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }
    const now = new Date().toISOString();
    const update: InventoryReplenishmentStatusUpdate = {
      id: 'demo-avoid-replenishment-task',
      created_at: now,
      updated_at: now,
      status: 'processing',
      message: '',
      trigger: 'query',
      file_name: null,
      file_size: null,
      total_rows: ids.length,
      failed_rows: 0,
      error_file: null,
    };
    inventoryAvoidReplenishmentUpdates.set(scope, update);
    const timer = setTimeout(() => {
      const completedUpdate: InventoryReplenishmentStatusUpdate = {
        ...update,
        status: 'completed',
        updated_at: new Date().toISOString(),
      };
      inventoryAvoidReplenishmentUpdates.set(scope, completedUpdate);
      inventoryAvoidReplenishmentUpdateTimers.delete(scope);
    }, 4000);
    inventoryAvoidReplenishmentUpdateTimers.set(scope, timer);
    return buildResponse({ task_id: 'demo-avoid-replenishment-task' }, config);
  }
  if (method === 'get' && cleanPath === '/v1/settings/avoid_replenishments/query') {
    return buildResponse({ task_id: 'demo-avoid-replenishment-task' }, config);
  }
  if (method === 'post' && cleanPath === '/v1/settings/avoid_replenishments/update/upload') {
    return buildResponse({ task_id: 'demo-avoid-replenishment-task' }, config);
  }
  if (method === 'get' && cleanPath === '/v1/settings/avoid_replenishments/update/status') {
    const scope = (params.scope as InventoryReplenishmentScope) ?? 'inventories';
    const update = inventoryAvoidReplenishmentUpdates.get(scope);
    const items = update ? [update] : [];
    return buildResponse(paginatedResponse(items, params), config);
  }
  if (
    method === 'get' &&
    cleanPath.startsWith('/v1/settings/avoid_replenishments/update/status/')
  ) {
    const scope = (params.scope as InventoryReplenishmentScope) ?? 'inventories';
    const update = inventoryAvoidReplenishmentUpdates.get(scope);
    if (update) {
      return buildResponse(update, config);
    }
    return buildResponse(
      {
        id: 'demo-avoid-replenishment-task',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        status: 'completed',
        message: '',
        trigger: 'query',
        file_name: null,
        file_size: null,
        total_rows: null,
        failed_rows: null,
        error_file: null,
      },
      config,
    );
  }
  if (method === 'post' && cleanPath === '/v1/settings/avoid_replenishments/download') {
    return buildResponse({ task_id: 'demo-avoid-replenishment-download' }, config);
  }
  if (method === 'get' && cleanPath === '/v1/settings/avoid_replenishments/summary') {
    const scope = (params.scope as string) ?? 'inventories';
    const inventories = await getInventoryInventoriesFromFlat();
    const locations = await getInventoryLocationsFromFlat();
    const skus = await getInventorySkusFromFlat();
    const productLocations = await getInventoryProductLocationsFromFlat();
    const options = await getInventoryOptionsFromFlat();
    const locationOverrides = inventoryAvoidReplenishmentState.locations;
    const skuOverrides = inventoryAvoidReplenishmentState.skus;
    const inventoryOverrides = inventoryAvoidReplenishmentState.inventories;
    const countInactive = (overrides: Map<string, boolean>) =>
      Array.from(overrides.values()).filter(Boolean).length;
    if (scope === 'locations') {
      const inactive = countInactive(locationOverrides);
      return buildResponse(
        {
          total: locations.length,
          active: Math.max(0, locations.length - inactive),
          inactive,
          product_locations: productLocations.length,
          styles: options.styles.length,
          assortment_groups: 0,
          categories: options.categories.length,
          seasons: options.seasons.length,
          total_sku_locations: inventories.length,
          active_sku_locations: inventories.length,
          inactive_sku_locations: 0,
        },
        config,
      );
    }
    if (scope === 'skus') {
      const inactive = countInactive(skuOverrides);
      return buildResponse(
        {
          active: Math.max(0, skus.length - inactive),
          active_sku_locations: inventories.length,
          assortment_groups: 0,
          categories: options.categories.length,
          departments: options.departments.length,
          inactive,
          inactive_sku_locations: 0,
          products: options.products.length,
          seasons: options.seasons.length,
          styles: options.styles.length,
          total: skus.length,
          total_sku_locations: inventories.length,
        },
        config,
      );
    }
    const inactive = countInactive(inventoryOverrides);
    return buildResponse(
      {
        active: Math.max(0, inventories.length - inactive),
        assortment_groups: 0,
        inactive,
        location_active: locations.length,
        location_inactive: 0,
        products: options.products.length,
        sku_active: skus.length,
        sku_inactive: 0,
        styles: options.styles.length,
        total: inventories.length,
        total_locations: locations.length,
        total_skus: skus.length,
      },
      config,
    );
  }
  if (method === 'put' && cleanPath.startsWith('/v1/tasks/abort/')) {
    const now = new Date().toISOString();
    inventoryAvoidReplenishmentUpdates.forEach((update, scope) => {
      if (update.status !== 'completed' && update.status !== 'failed') {
        const cancelledUpdate: InventoryReplenishmentStatusUpdate = {
          ...update,
          status: 'cancelled',
          updated_at: now,
        };
        inventoryAvoidReplenishmentUpdates.set(scope, cancelledUpdate);
        const existingTimer = inventoryAvoidReplenishmentUpdateTimers.get(scope);
        if (existingTimer) {
          clearTimeout(existingTimer);
        }
        inventoryAvoidReplenishmentUpdateTimers.delete(scope);
      }
    });
    return buildResponse({ success: true }, config);
  }
  if (method === 'get' && cleanPath === '/inventory/inventories') {
    const inventories = await getInventoryInventoriesFromFlat();
    const inventoryOverrides = inventoryAvoidReplenishmentState.inventories;
    const locationOverrides = inventoryAvoidReplenishmentState.locations;
    const skuOverrides = inventoryAvoidReplenishmentState.skus;
    const decorated = inventories.map((item) => ({
      ...item,
      avoid_replenishment: inventoryOverrides.get(item.id) ?? item.avoid_replenishment,
      location_avoid_replenishment:
        locationOverrides.get(item.location_id) ?? item.location_avoid_replenishment,
      sku_avoid_replenishment: skuOverrides.get(item.sku_id) ?? item.sku_avoid_replenishment,
    }));
    return buildResponse(listResponse(decorated, params), config);
  }
  if (method === 'get' && cleanPath === '/inventory/product-locations') {
    const productLocations = await getInventoryProductLocationsFromFlat();
    return buildResponse(listResponse(productLocations, params), config);
  }
  if (method === 'get' && cleanPath === '/inventory/locations') {
    const inventoryLocations = await getInventoryLocationsFromFlat();
    const overrides = inventoryAvoidReplenishmentState.locations;
    const decorated = inventoryLocations.map((item) => ({
      ...item,
      avoid_replenishment: overrides.get(item.id) ?? item.avoid_replenishment,
    }));
    return buildResponse(listResponse(decorated, params), config);
  }
  if (method === 'post' && cleanPath === '/inventory/locations/by-ids') {
    const inventoryLocations = await getInventoryLocationsFromFlat();
    const ids = (data?.ids ?? []) as string[];
    const overrides = inventoryAvoidReplenishmentState.locations;
    const filtered = inventoryLocations
      .filter((item) => ids.includes(item.id))
      .map((item) => ({
        ...item,
        avoid_replenishment: overrides.get(item.id) ?? item.avoid_replenishment,
      }));
    return buildResponse(filtered, config);
  }
  if (method === 'get' && cleanPath === '/inventory/skus') {
    const inventorySkus = await getInventorySkusFromFlat();
    const overrides = inventoryAvoidReplenishmentState.skus;
    const decorated = inventorySkus.map((item) => ({
      ...item,
      avoid_replenishment: overrides.get(item.id) ?? item.avoid_replenishment,
    }));
    return buildResponse(listResponse(decorated, params), config);
  }
  if (method === 'post' && cleanPath === '/inventory/skus/by-ids') {
    const ids = (data?.ids ?? []) as string[];
    const inventorySkus = await getInventorySkusFromFlat();
    const overrides = inventoryAvoidReplenishmentState.skus;
    const filtered = inventorySkus
      .filter((item) => ids.includes(item.id))
      .map((item) => ({
        ...item,
        avoid_replenishment: overrides.get(item.id) ?? item.avoid_replenishment,
      }));
    return buildResponse(filtered, config);
  }
  if (method === 'get' && cleanPath === '/inventory/products') {
    const inventoryOptions = await getInventoryOptionsFromFlat();
    return buildResponse(listResponse(ensureExternalId(inventoryOptions.products), params), config);
  }
  if (method === 'post' && cleanPath === '/inventory/products/by-ids') {
    const ids = (data?.ids ?? []) as string[];
    const inventoryOptions = await getInventoryOptionsFromFlat();
    return buildResponse(
      ensureExternalId(inventoryOptions.products).filter((item) => ids.includes(item.id)),
      config,
    );
  }
  const options = await getInventoryOptionsFromFlat();
  if (method === 'get' && cleanPath === '/inventory/brands') {
    const source = options.brands;
    return buildResponse(listResponse(source, params), config);
  }
  if (method === 'post' && cleanPath === '/inventory/brands/by-ids') {
    const source = options.brands;
    const ids = (data?.ids ?? []) as string[];
    return buildResponse(
      source.filter((item) => ids.includes(item.id)),
      config,
    );
  }
  if (method === 'get' && cleanPath === '/inventory/seasons') {
    const source = options.seasons;
    return buildResponse(listResponse(source, params), config);
  }
  if (method === 'post' && cleanPath === '/inventory/seasons/by-ids') {
    const source = options.seasons;
    const ids = (data?.ids ?? []) as string[];
    return buildResponse(
      source.filter((item) => ids.includes(item.id)),
      config,
    );
  }
  if (method === 'get' && cleanPath === '/inventory/styles') {
    const source = options.styles;
    return buildResponse(listResponse(source, params), config);
  }
  if (method === 'post' && cleanPath === '/inventory/styles/by-ids') {
    const source = options.styles;
    const ids = (data?.ids ?? []) as string[];
    return buildResponse(
      source.filter((item) => ids.includes(item.id)),
      config,
    );
  }
  if (method === 'get' && cleanPath === '/inventory/categories') {
    const source = options.categories;
    return buildResponse(listResponse(source, params), config);
  }
  if (method === 'get' && cleanPath === '/inventory/colors') {
    const source = options.colors ?? [];
    return buildResponse(listResponse(source, params), config);
  }
  if (method === 'post' && cleanPath === '/inventory/colors/by-ids') {
    const source = options.colors ?? [];
    const ids = (data?.ids ?? []) as string[];
    return buildResponse(
      source.filter((item) => ids.includes(item.id)),
      config,
    );
  }
  if (method === 'post' && cleanPath === '/inventory/categories/by-ids') {
    const source = options.categories;
    const ids = (data?.ids ?? []) as string[];
    return buildResponse(
      source.filter((item) => ids.includes(item.id)),
      config,
    );
  }
  if (method === 'get' && cleanPath === '/inventory/departments') {
    const source = options.departments;
    return buildResponse(listResponse(source, params), config);
  }
  if (method === 'post' && cleanPath === '/inventory/departments/by-ids') {
    const source = options.departments;
    const ids = (data?.ids ?? []) as string[];
    return buildResponse(
      source.filter((item) => ids.includes(item.id)),
      config,
    );
  }
  if (method === 'get' && cleanPath === '/inventory/regions') {
    const source = options.regions;
    return buildResponse(listResponse(source, params), config);
  }
  if (method === 'get' && cleanPath === '/inventory/source-locations') {
    const source = options.sourceLocations ?? [];
    return buildResponse(listResponse(source, params), config);
  }
  if (method === 'post' && cleanPath === '/inventory/source-locations/by-ids') {
    const source = options.sourceLocations ?? [];
    const ids = (data?.ids ?? []) as string[];
    return buildResponse(
      source.filter((item) => ids.includes(item.id)),
      config,
    );
  }
  if (method === 'get' && cleanPath === '/inventory/cities') {
    const source = options.cities;
    return buildResponse(listResponse(source, params), config);
  }
  if (method === 'get' && cleanPath === '/inventory/classifications') {
    const source = options.classifications ?? [];
    return buildResponse(listResponse(source, params), config);
  }
  if (method === 'get' && cleanPath === '/inventory/assortment-groups') {
    const source = options.assortmentGroups ?? [];
    return buildResponse(listResponse(source, params), config);
  }
  if (method === 'post' && cleanPath === '/inventory/assortment-groups/by-ids') {
    const source = options.assortmentGroups ?? [];
    const ids = (data?.ids ?? []) as string[];
    return buildResponse(
      source.filter((item) => ids.includes(item.id)),
      config,
    );
  }
  if (method === 'get' && cleanPath === '/inventory/sizes') {
    const inventories = await getInventoryInventoriesFromFlat();
    const sizes = Array.from(new Set(inventories.map((item) => item.size).filter(Boolean))).map(
      (size) => ({ id: size, name: size }),
    );
    return buildResponse(listResponse(sizes, params), config);
  }
  if (method === 'post' && cleanPath === '/inventory/sizes/by-ids') {
    const inventories = await getInventoryInventoriesFromFlat();
    const sizes = Array.from(new Set(inventories.map((item) => item.size).filter(Boolean))).map(
      (size) => ({ id: size, name: size }),
    );
    const ids = (data?.ids ?? []) as string[];
    return buildResponse(
      sizes.filter((item) => ids.includes(item.id)),
      config,
    );
  }
  return null;
}
function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}
function resolveRelativeDate(value: string) {
  const match = value.match(/Today\s*-\s*(\d+)\s*days/i);
  if (!match) {
    return value;
  }
  const days = Number(match[1]);
  if (!Number.isFinite(days)) {
    return value;
  }
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString();
}
function buildAllocationsGroups(): AllocationsGroup[] {
  return allocationGroups.map((group) => {
    const id = slugify(group.name);
    const isCalculated = group.status === 'Edit & Approve';
    const updatedAt = resolveRelativeDate(group.updated_at);
    const createdAt = resolveRelativeDate(group.created_at);
    if (isCalculated) {
      return {
        id,
        kind: 'product_groups',
        status: 'calculated',
        name: group.name,
        updated_by: { id: 'demo-user', name: group.updated_by },
        created_by: { id: 'demo-user', name: group.created_by },
        assortment_groups_count: group.assortment_groups,
        products_count: group.products,
        units: group.units,
        created_at: createdAt,
        updated_at: updatedAt,
      } satisfies AllocationsGroup;
    }
    return {
      id: null,
      kind: 'products',
      status: 'pending',
      name: group.name,
      updated_by: { id: 'demo-user', name: group.updated_by },
      created_by: { id: 'demo-user', name: group.created_by },
      assortment_groups_count: group.assortment_groups,
      products_count: group.products,
      units: group.units,
      created_at: createdAt,
      updated_at: updatedAt,
    } satisfies AllocationsGroup;
  });
}
function buildAllocationsInfo(groups: AllocationsGroup[]): AllocationsInfo {
  return {
    has_entries: groups.length > 0,
    unassigned_allocation_assortment_groups_count: 0,
    unassigned_allocation_products_count: 0,
  };
}
async function handleAllocations(
  method: Method,
  path: string,
  config?: AxiosRequestConfig,
  data?: any,
) {
  const params = mergeParams(config, path);
  const cleanPath = normalizePath(path).split('?')[0];
  if (method === 'get' && cleanPath === '/v1/allocations/info') {
    const groups = buildAllocationsGroups();
    return buildResponse({ data: buildAllocationsInfo(groups) }, config);
  }
  if (method === 'get' && cleanPath === '/v1/allocations/groups') {
    return buildResponse({ data: buildAllocationsGroups() }, config);
  }
  if (method === 'post' && cleanPath === '/v1/allocations/groups') {
    return buildResponse({ allocation_group_id: `demo-${Date.now()}` }, config);
  }
  if (
    method === 'patch' &&
    cleanPath.startsWith('/v1/allocations/groups/') &&
    cleanPath.endsWith('/approve')
  ) {
    return buildResponse({}, config);
  }
  if (method === 'post' && cleanPath.endsWith('/recalculate')) {
    return buildResponse({}, config);
  }
  if (method === 'post' && cleanPath === '/v1/allocations/groups/destroy') {
    return buildResponse({}, config);
  }
  if (method === 'patch' && cleanPath.startsWith('/v1/allocations/groups/')) {
    const id = cleanPath.split('/').pop() ?? '';
    const groups = buildAllocationsGroups();
    const group = groups.find((item) => item.id === id) ?? groups[0];
    if (group) {
      return buildResponse({ ...group, name: data?.name ?? group.name }, config);
    }
  }
  if (method === 'get' && cleanPath === '/v1/allocations/settings') {
    const settings: AllocationsSettings = {
      last_calculation_time: new Date().toISOString(),
      is_calculation_in_progress: allocationsCalculationInProgress,
      is_recalculation_available: !allocationsCalculationInProgress,
      is_allocation_data_present: true,
      bypass_allocation_calculation_in_progress: false,
    };
    return buildResponse({ data: settings }, config);
  }
  if (method === 'post' && cleanPath === '/v1/allocations/calculations') {
    allocationsCalculationInProgress = true;
    setTimeout(() => {
      allocationsCalculationInProgress = false;
    }, 2000);
    return buildResponse({}, config);
  }
  if (method === 'get' && cleanPath === '/v1/allocations/assortment_groups') {
    const groups = await getAllocationsAssortmentGroupsFromFlat();
    return buildResponse(listResponse(groups, params), config);
  }
  if (method === 'get' && cleanPath === '/v1/allocations/similar_products') {
    const items = await getAllocationsSimilarProductsFromFlat();
    return buildResponse(paginatedResponse(items, params), config);
  }
  if (
    method === 'get' &&
    cleanPath.startsWith('/v1/allocations/products/') &&
    cleanPath.endsWith('/similar_products')
  ) {
    const parts = cleanPath.split('/');
    const productId = parts[4] ?? '';
    const list = await getAllocationsSimilarProductsFromFlat();
    const item = list.find((entry) => entry.id === productId);
    if (!item) {
      return buildResponse(null, config);
    }
    const comparable = await getAllocationsComparableProductsFromFlat(productId);
    const payload: AllocationsProductSimilarities = {
      id: productId,
      external_id: productId,
      department_names: item.department_name,
      category_names: item.category_name,
      name: item.name,
      images: item.images,
      top_similar_products: comparable.map((product, index) => ({
        ...product,
        rank: index + 1,
      })),
    };
    return buildResponse(payload, config);
  }
  if (
    method === 'get' &&
    cleanPath.startsWith('/v1/allocations/groups/') &&
    cleanPath.endsWith('/assortment_group_locations')
  ) {
    const items = await getAllocationsAssortmentGroupLocationsFromFlat();
    return buildResponse(paginatedResponse(items, params), config);
  }
  if (method === 'get' && cleanPath.startsWith('/v1/allocations/groups/')) {
    const id = cleanPath.split('/').pop() ?? '';
    const groups = buildAllocationsGroups();
    const group = groups.find((item) => item.id === id);
    if (group) {
      return buildResponse(group, config);
    }
    return buildResponse(
      {
        ...groups[0],
        id,
        kind: 'product_groups',
        status: 'calculated',
        name: 'Demo Allocation Group',
      },
      config,
    );
  }
  if (method === 'post' && cleanPath.endsWith('/assortment_group_locations/by_ids')) {
    const items = await getAllocationsAssortmentGroupLocationsFromFlat();
    const ids = (data?.ids ?? []) as {
      location_id: string;
      assortment_group_id: string;
    }[];
    const matched = items.filter((item) =>
      ids.some(
        (target) =>
          item.location_id === target.location_id &&
          item.assortment_group_id === target.assortment_group_id,
      ),
    );
    return buildResponse(paginatedResponse(matched, { page: 1, per_page: matched.length }), config);
  }
  if (method === 'get' && cleanPath === '/allocations/locations') {
    const allocationOptions = await getAllocationsFilterOptionsFromFlat();
    const view = params.view as string | undefined;
    if (view === 'minimal') {
      return buildResponse(listResponse(allocationOptions.locations, params), config);
    }
    const locations = await getAllocationsInventoryLocationsFromFlat();
    return buildResponse(listResponse(locations, params), config);
  }
  if (method === 'post' && cleanPath === '/allocations/locations/by-ids') {
    const ids = (data?.ids ?? []) as string[];
    const locations = await getAllocationsInventoryLocationsFromFlat();
    const filtered = locations.filter((item) => ids.includes(item.id));
    return buildResponse(filtered, config);
  }
  if (method === 'get' && cleanPath === '/allocations/departments') {
    const allocationOptions = await getAllocationsFilterOptionsFromFlat();
    return buildResponse(listResponse(allocationOptions.departments, params), config);
  }
  if (method === 'post' && cleanPath === '/allocations/departments/by-ids') {
    const allocationOptions = await getAllocationsFilterOptionsFromFlat();
    const ids = (data?.ids ?? []) as string[];
    return buildResponse(
      allocationOptions.departments.filter((item) => ids.includes(item.id)),
      config,
    );
  }
  if (method === 'get' && cleanPath === '/allocations/categories') {
    const allocationOptions = await getAllocationsFilterOptionsFromFlat();
    return buildResponse(listResponse(allocationOptions.categories, params), config);
  }
  if (method === 'post' && cleanPath === '/allocations/categories/by-ids') {
    const allocationOptions = await getAllocationsFilterOptionsFromFlat();
    const ids = (data?.ids ?? []) as string[];
    return buildResponse(
      allocationOptions.categories.filter((item) => ids.includes(item.id)),
      config,
    );
  }
  if (method === 'get' && cleanPath === '/allocations/brands') {
    const allocationOptions = await getAllocationsFilterOptionsFromFlat();
    return buildResponse(listResponse(allocationOptions.brands, params), config);
  }
  if (method === 'get' && cleanPath === '/allocations/seasons') {
    const allocationOptions = await getAllocationsFilterOptionsFromFlat();
    return buildResponse(listResponse(allocationOptions.seasons, params), config);
  }
  if (method === 'get' && cleanPath === '/allocations/styles') {
    return buildResponse(listResponse([], params), config);
  }
  if (method === 'get' && cleanPath === '/allocations/classifications') {
    const allocationOptions = await getAllocationsFilterOptionsFromFlat();
    return buildResponse(listResponse(allocationOptions.classifications, params), config);
  }
  if (method === 'get' && cleanPath === '/allocations/regions') {
    const allocationOptions = await getAllocationsFilterOptionsFromFlat();
    return buildResponse(listResponse(allocationOptions.regions, params), config);
  }
  if (method === 'get' && cleanPath === '/allocations/cities') {
    const allocationOptions = await getAllocationsFilterOptionsFromFlat();
    return buildResponse(listResponse(allocationOptions.cities, params), config);
  }
  if (method === 'get' && cleanPath === '/allocations/assortment-groups') {
    const allocationOptions = await getAllocationsFilterOptionsFromFlat();
    return buildResponse(listResponse(allocationOptions.assortmentGroups, params), config);
  }
  if (method === 'get' && cleanPath === '/allocations/products') {
    const view = params.view as string | undefined;
    if (view === 'minimal') {
      const allocationOptions = await getAllocationsFilterOptionsFromFlat();
      return buildResponse(listResponse(allocationOptions.products, params), config);
    }
    const products = await getAllocationsProductsFromFlat();
    if (params.filter) {
      params.filter = params.filter.replace(
        /\bsource_location_id\b/g,
        'source_location_external_id',
      );
    }
    return buildResponse(listResponse(products, params), config);
  }
  if (method === 'post' && cleanPath === '/allocations/products/by-ids') {
    const allocationOptions = await getAllocationsFilterOptionsFromFlat();
    const ids = (data?.ids ?? []) as string[];
    return buildResponse(
      allocationOptions.products.filter((item) => ids.includes(item.id)),
      config,
    );
  }
  if (
    method === 'get' &&
    cleanPath.startsWith('/allocations/products/') &&
    cleanPath.endsWith('/comparable-products')
  ) {
    const productId = cleanPath.split('/')[3] ?? '';
    void productId;
    const items = await getAllocationsComparableProductsCatalogFromFlat();
    return buildResponse(listResponse(items, params), config);
  }
  if (method === 'get' && cleanPath === '/allocations/groups/pending/product-locations') {
    const items = await getAllocationsProductLocationsFromFlat();
    const allocationGroupId = resolveAllocationGroupId(params);
    const decorated = items.map((item) => ({
      ...item,
      allocation_group_id: allocationGroupId,
      state: 'pending',
    }));
    return buildResponse(listResponse(decorated, params), config);
  }
  if (method === 'get' && cleanPath === '/allocations/groups/approved/product-locations') {
    const items = await getAllocationsProductLocationsFromFlat();
    const allocationGroupId = resolveAllocationGroupId(params);
    const decorated = items.map((item) => ({
      ...item,
      allocation_group_id: allocationGroupId,
      state: 'approved',
    }));
    return buildResponse(listResponse(decorated, params), config);
  }
  if (
    method === 'get' &&
    cleanPath.includes('/assortment-groups/') &&
    cleanPath.endsWith('/product-locations')
  ) {
    const parts = cleanPath.split('/');
    const assortmentGroupId = parts[5] ?? '';
    const items = await getAllocationsProductLocationsFromFlat();
    const filtered = items.filter(
      (item) => slugify(item.assortment_group_name ?? '') === assortmentGroupId,
    );
    return buildResponse(listResponse(filtered, params), config);
  }
  if (method === 'get' && cleanPath === '/v1/allocations/product_locations/pending') {
    const items = await getAllocationsProductLocationsFromFlat();
    const allocationGroupId = extractAllocationGroupId(params.filter as string | undefined);
    const decorated = items.map((item) => ({
      ...item,
      allocation_group_id: allocationGroupId,
      state: 'pending',
    }));
    return buildResponse(paginatedResponse(decorated, params), config);
  }
  if (method === 'get' && cleanPath === '/v1/allocations/product_locations/approved') {
    const items = await getAllocationsProductLocationsFromFlat();
    const allocationGroupId = extractAllocationGroupId(params.filter as string | undefined);
    const decorated = items.map((item) => ({
      ...item,
      allocation_group_id: allocationGroupId,
      state: 'approved',
    }));
    return buildResponse(paginatedResponse(decorated, params), config);
  }
  if (method === 'get' && cleanPath.startsWith('/v1/allocations/product_locations/kpis/')) {
    const items = await getAllocationsProductLocationsFromFlat();
    return buildResponse(
      {
        data: {
          locations_count: new Set(items.map((item) => item.location_id)).size,
          products_count: new Set(items.map((item) => item.product_id)).size,
          skus_count: items.length,
          failed_in_calculation_number: 0,
          insufficient_wh_product_locations_number: 0,
          expired_product_locations_number: 0,
        },
      },
      config,
    );
  }
  if (method === 'get' && cleanPath.startsWith('/v1/allocations/product_locations/')) {
    const id = cleanPath.split('/').pop() ?? '';
    const details = await getAllocationsProductLocationDetailsFromFlat(id);
    return buildResponse({ data: details }, config);
  }
  if (method === 'get' && cleanPath === '/v1/allocations/product/products_list') {
    const list = await getAllocationsProductsListFromFlat();
    return buildResponse({ data: list }, config);
  }
  if (method === 'get' && cleanPath === '/v1/allocations/product/products_navigation') {
    const productId = params.product_id as string;
    const nav = await getAllocationsProductsNavigationFromFlat(productId);
    return buildResponse({ data: nav }, config);
  }
  if (
    method === 'get' &&
    cleanPath.startsWith('/v1/allocations/product/') &&
    cleanPath.endsWith('/summary/')
  ) {
    const productId = cleanPath.split('/')[4] ?? '';
    const summary = await getAllocationsProductSummaryFromFlat(productId);
    return buildResponse(summary, config);
  }
  if (
    method === 'get' &&
    cleanPath.startsWith('/v1/allocations/product/') &&
    cleanPath.endsWith('/download')
  ) {
    const content = 'product_id,allocation_units\n';
    const blob = new Blob([content], { type: 'text/csv' });
    return buildResponse(blob, config);
  }
  if (
    method === 'get' &&
    cleanPath.startsWith('/v1/allocations/product/') &&
    !cleanPath.includes('/summary')
  ) {
    const productId = cleanPath.split('/')[4] ?? '';
    const list = await getAllocationsProductLocationsListFromFlat(productId);
    return buildResponse(list, config);
  }
  if (method === 'post' && cleanPath === '/v1/allocations/product_locations/download.csv') {
    const content = 'product_id,location_id,units\n';
    const blob = new Blob([content], { type: 'text/csv' });
    return buildResponse(blob, config);
  }
  if (
    method === 'post' &&
    cleanPath === '/v1/allocations/product_locations/approved/download.csv'
  ) {
    const content = 'product_id,location_id,units\n';
    const blob = new Blob([content], { type: 'text/csv' });
    return buildResponse(blob, config);
  }
  if (method === 'post' && cleanPath === '/v1/allocations/product_locations/pending/download.csv') {
    const content = 'product_id,location_id,units\n';
    const blob = new Blob([content], { type: 'text/csv' });
    return buildResponse(blob, config);
  }
  if (method === 'get' && cleanPath === '/allocations/product-locations/pending') {
    const items = await getAllocationsProductLocationsFromFlat();
    return buildResponse(listResponse(items, params), config);
  }
  if (method === 'get' && cleanPath === '/allocations/product-locations/approved') {
    const items = await getAllocationsProductLocationsFromFlat();
    return buildResponse(listResponse(items, params), config);
  }
  if (
    method === 'get' &&
    cleanPath.startsWith('/v1/allocations/products/') &&
    cleanPath.endsWith('/kpis')
  ) {
    const productId = cleanPath.split('/')[4] ?? '';
    const kpis = await getAllocationsProductKpiFromFlat(productId);
    return buildResponse(kpis, config);
  }
  if (method === 'patch' && cleanPath === '/v1/allocations/product_locations') {
    applyAllocationsSkuOverrides(data?.bundle_entities ?? []);
    return buildResponse({ data: data?.bundle_entities ?? [] }, config);
  }
  if (method === 'post' && cleanPath === '/v1/allocations/product_locations/singles_bulk_update') {
    applyAllocationsSizeOverrides(data?.ids ?? [], data?.quantities ?? {});
    return buildResponse({ data: [] }, config);
  }
  if (method === 'post' && cleanPath === '/v1/allocations/product_locations/packs_bulk_update') {
    return buildResponse({ data: [] }, config);
  }
  if (method === 'patch' && cleanPath === '/v1/allocations/product_locations/approve') {
    return buildResponse({ data: [] }, config);
  }
  if (method === 'delete' && cleanPath === '/v1/allocations/product_locations') {
    return buildResponse({ data: { ids: data?.ids ?? [] } }, config);
  }
  return null;
}
async function handleSpecialEvents(
  method: Method,
  path: string,
  config?: AxiosRequestConfig,
  data?: any,
) {
  const params = mergeParams(config, path);
  const cleanPath = normalizePath(path).split('?')[0];
  if (method === 'get' && cleanPath === '/special-events') {
    const events = await getSpecialEventsFromFlat();
    const searched = applySearch(events, params.search, ['name', 'type', 'kind', 'segment']);
    const sorted = applySort(searched, params.sortBy, params.sortOrder);
    const result = paginate(sorted, params.limit ?? 25, params.offset ?? 0);
    return buildResponse(result, config);
  }
  if (method === 'get' && cleanPath === '/v1/special_events') {
    const events = await getSpecialEventsFromFlat();
    const stage = params.stage as string | undefined;
    const filtered = stage ? events.filter((e) => e.stage === stage) : events;
    const searched = params.q
      ? filtered.filter((e) => e.name.toLowerCase().includes(String(params.q).toLowerCase()))
      : filtered;
    return buildResponse(paginatedResponse(searched, params), config);
  }
  if (method === 'post' && cleanPath === '/v1/special_events/by_ids') {
    const events = await getSpecialEventsFromFlat();
    const ids = (data?.ids ?? []) as string[];
    return buildResponse({ data: events.filter((e) => ids.includes(e.id)) }, config);
  }
  if (
    method === 'get' &&
    cleanPath.startsWith('/v1/special_events/') &&
    cleanPath.endsWith('/special_event_inventories')
  ) {
    const parts = cleanPath.split('/');
    const eventId = parts[3] ?? '';
    const inventories = await getSpecialEventInventoriesFromFlat(eventId);
    return buildResponse(paginatedResponse(inventories, params), config);
  }
  if (method === 'get' && cleanPath.startsWith('/v1/special_events/')) {
    const eventId = cleanPath.split('/').pop() ?? '';
    const details = await getSpecialEventDetailsFromFlat(eventId);
    if (details) {
      return buildResponse({ data: details }, config);
    }
    return buildResponse({ data: null }, config);
  }
  if (
    method === 'get' &&
    cleanPath.startsWith('/special-events/') &&
    cleanPath.endsWith('/inventories')
  ) {
    const parts = cleanPath.split('/');
    const eventId = parts[2] ?? '';
    const inventories = await getSpecialEventInventoriesFromFlat(eventId);
    return buildResponse(listResponse(inventories, params), config);
  }
  if (method === 'post' && cleanPath === '/analytics/dashboards/pre-event-analysis') {
    const filters = data as Record<string, unknown> | undefined;
    const dashboard = await getSpecialEventsAnalysisDashboardFromFlat(filters);
    return buildResponse(dashboard, config);
  }
  if (method === 'get' && cleanPath.startsWith('/analytics/dashboards/pre-event-analysis/')) {
    const filterName = cleanPath.split('/').pop() ?? '';
    const options = await getSpecialEventsAnalysisFilterOptionsFromFlat(filterName);
    const searched = params.search
      ? options.filter((o) => o.toLowerCase().includes(String(params.search).toLowerCase()))
      : options;
    return buildResponse({ data: searched }, config);
  }
  if (method === 'post' && cleanPath === '/v1/special_events/approve') {
    return buildResponse({}, config);
  }
  if (method === 'post' && cleanPath === '/v1/special_events/download') {
    return buildResponse({ task_id: 'demo-special-events-download' }, config);
  }
  if (
    method === 'post' &&
    cleanPath.startsWith('/v1/special_events/') &&
    cleanPath.endsWith('/recalculate')
  ) {
    return buildResponse({}, config);
  }
  if (method === 'delete' && cleanPath === '/v1/special_events') {
    return buildResponse({}, config);
  }
  if (method === 'patch' && cleanPath === '/v1/special_events/reject') {
    return buildResponse({}, config);
  }
  if (
    method === 'post' &&
    cleanPath.startsWith('/v1/special_events/') &&
    cleanPath.endsWith('/special_event_inventories')
  ) {
    return buildResponse({}, config);
  }
  if (
    method === 'delete' &&
    cleanPath.startsWith('/v1/special_events/') &&
    cleanPath.endsWith('/special_event_inventories')
  ) {
    return buildResponse({}, config);
  }
  return null;
}
export async function handleDemoProxyRequest(
  method: Method,
  url: string,
  config?: AxiosRequestConfig,
  data?: any,
): Promise<AxiosResponse | null> {
  const path = normalizePath(url);
  const allocations = await handleAllocations(method, path, config, data);
  if (allocations) {
    return allocations;
  }
  const replenishment = await handleReplenishment(method, path, config, data);
  if (replenishment) {
    return replenishment;
  }
  const inventory = await handleInventory(method, path, config, data);
  if (inventory) {
    return inventory;
  }
  const events = await handleSpecialEvents(method, path, config, data);
  if (events) {
    return events;
  }
  return null;
}
